import { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { SelectInput, MultiSelectInput, Button, realCarrierName } from '../../../components';
import {
    calculateCutoffTime,
    detectScheduleType,
    SCHEDULE_TYPE_CARRIER,
    SCHEDULE_TYPE_CROSS_CARRIER,
    SCHEDULE_TYPE_SHIPPING_METHOD,
    getTimezoneUtcOffset
} from '../utils';

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

const T_PREFIX = 'lastmile_management.dispatch_schedules';

const parseDispatchTime = (timeStr) => {
    if (!timeStr) return '';
    const match = timeStr.match(/(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
    return '';
};

const buildDispatchTime = (timeValue, warehouseTimeZone) => {
    if (!timeValue) return '';
    const offset = getTimezoneUtcOffset(warehouseTimeZone);
    return `${timeValue}:00${offset}`;
};

const parseISO8601Duration = (duration) => {
    if (!duration) return { value: '', unit: 'minutes' };
    const match = String(duration).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
    if (!match) return { value: '', unit: 'minutes' };
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    if (hours > 0 && minutes === 0) return { value: String(hours), unit: 'hours' };
    return { value: String(hours * 60 + minutes), unit: 'minutes' };
};

const buildISO8601Duration = (value, unit) => {
    const num = parseInt(value, 10);
    if (!num || num <= 0) return '';
    if (unit === 'hours') return `PT${num}H`;
    return `PT${num}M`;
};

const NO_FILTER_OPTION_ID = '__none__';

export const DispatchScheduleFormModal = ({
    open,
    setOpen,
    scheduleData = null,
    defaultValues = null,
    onSubmit,
    isLoading = false,
    carrierOptions = [],
    channelTypes = [],
    availableStates = [],
    shippingMethods = [],
    warehouseTimeZone = null
}) => {
    const { i18n } = useTranslation();
    const cancelButtonRef = useRef(null);
    const initializedRef = useRef(false);
    const isEditing = !!scheduleData;

    const [scheduleType, setScheduleType] = useState(SCHEDULE_TYPE_CARRIER);
    const [formData, setFormData] = useState({
        carrier_name: null,
        dispatch_day: null,
        dispatch_days: new Set(),
        dispatch_time: '',
        time_before_value: '',
        time_before_unit: 'minutes',
        channel_type: null,
        shipping_state: [],
        shipping_method: null
    });
    const [errors, setErrors] = useState({});

    const dayOptions = useMemo(() =>
        DAYS_OF_WEEK.map(d => ({
            id: d,
            name: i18n.t(`lastmile_management.days.${d}`)
        })),
    [i18n]);

    const unitOptions = useMemo(() => [
        { id: 'hours', name: i18n.t(`${T_PREFIX}.duration_units.unit_hours`) },
        { id: 'minutes', name: i18n.t(`${T_PREFIX}.duration_units.unit_minutes`) }
    ], [i18n]);

    const carrierSelectOptions = useMemo(() =>
        carrierOptions.map(name => ({ id: name, name: name })),
    [carrierOptions]);

    const channelTypeSelectOptions = useMemo(() =>
        channelTypes.map(ct => ({ id: ct.id, name: ct.name })),
    [channelTypes]);

    const shippingMethodSelectOptions = useMemo(() =>
        shippingMethods.map(sm => ({ id: sm.id, name: `${sm.shipping_name}${sm.carrier_name ? ` (${sm.carrier_name})` : ''}` })),
    [shippingMethods]);

    const stateSelectOptionsWithAll = useMemo(() => [
        { id: NO_FILTER_OPTION_ID, name: i18n.t(`${T_PREFIX}.form.all_states`) },
        ...availableStates.map(s => ({ id: s.code, name: `${s.code} - ${s.name}` }))
    ], [availableStates, i18n]);

    const stateSelectOptions = useMemo(() =>
        availableStates.map(s => ({ id: s.code, name: `${s.code} - ${s.name}` })),
    [availableStates]);

    useEffect(() => {
        if (!open) {
            initializedRef.current = false;
            return;
        }
        if (initializedRef.current) return;
        initializedRef.current = true;

        if (scheduleData) {
            const detectedType = detectScheduleType(scheduleData);
            setScheduleType(detectedType);

            // Normalize to the REAL carrier so it matches the carrier options and
            // is persisted as "UBER", not the "Cubbo" whitelabel (CUBBO-2185).
            const scheduleCarrier = realCarrierName(scheduleData.carrier_name, scheduleData.internal_carrier_name);
            const carrierOption = scheduleCarrier
                ? carrierSelectOptions.find(c => c.id === scheduleCarrier)
                    || { id: scheduleCarrier, name: scheduleCarrier }
                : null;
            const dayOption = dayOptions.find(d => d.id === scheduleData.dispatch_day) || null;
            const parsed = parseISO8601Duration(scheduleData.time_before_dispatch);

            const channelTypeOption = scheduleData.channel_type_id
                ? channelTypeSelectOptions.find(ct => ct.id === scheduleData.channel_type_id)
                    || { id: scheduleData.channel_type_id, name: scheduleData.channel_type_name || String(scheduleData.channel_type_id) }
                : null;
            const stateOption = scheduleData.shipping_state
                ? stateSelectOptionsWithAll.find(s => s.id === scheduleData.shipping_state) || null
                : { id: NO_FILTER_OPTION_ID, name: i18n.t(`${T_PREFIX}.form.all_states`) };
            const shippingMethodOption = scheduleData.shipping_method_id
                ? shippingMethodSelectOptions.find(sm => sm.id === scheduleData.shipping_method_id)
                    || { id: scheduleData.shipping_method_id, name: scheduleData.shipping_method_name || `ID ${scheduleData.shipping_method_id}` }
                : null;

            setFormData({
                carrier_name: carrierOption,
                dispatch_day: dayOption,
                dispatch_days: new Set(),
                dispatch_time: parseDispatchTime(scheduleData.dispatch_time),
                time_before_value: parsed.value,
                time_before_unit: parsed.unit,
                channel_type: channelTypeOption,
                shipping_state: stateOption,
                shipping_method: shippingMethodOption
            });
        } else {
            const prefilled = {
                carrier_name: null,
                dispatch_day: null,
                dispatch_days: new Set(),
                dispatch_time: '',
                time_before_value: '',
                time_before_unit: 'minutes',
                channel_type: null,
                shipping_state: [],
                shipping_method: null
            };

            let prefilledType = SCHEDULE_TYPE_CARRIER;

            if (defaultValues) {
                if (defaultValues.carrier_name) {
                    prefilled.carrier_name = carrierSelectOptions.find(c => c.id === defaultValues.carrier_name) || null;
                }
                if (defaultValues.dispatch_day !== null && defaultValues.dispatch_day !== undefined) {
                    prefilled.dispatch_days = new Set([defaultValues.dispatch_day]);
                }
                if (defaultValues.channel_type_id) {
                    prefilled.channel_type = channelTypeSelectOptions.find(ct => ct.id === defaultValues.channel_type_id) || null;
                }
                if (defaultValues.shipping_state) {
                    const found = stateSelectOptions.find(s => s.id === defaultValues.shipping_state);
                    prefilled.shipping_state = found ? [found] : [];
                }
                if (defaultValues.shipping_method_id) {
                    prefilled.shipping_method = shippingMethodSelectOptions.find(sm => sm.id === defaultValues.shipping_method_id) || null;
                }
                if (defaultValues.dispatch_time) {
                    prefilled.dispatch_time = parseDispatchTime(defaultValues.dispatch_time);
                }
                if (defaultValues.time_before_dispatch) {
                    const parsed = parseISO8601Duration(defaultValues.time_before_dispatch);
                    prefilled.time_before_value = parsed.value;
                    prefilled.time_before_unit = parsed.unit;
                }
                if (defaultValues.schedule_type) {
                    prefilledType = defaultValues.schedule_type;
                }
            }

            setScheduleType(prefilledType);
            setFormData(prefilled);
        }
        setErrors({});
    }, [open, scheduleData, defaultValues, carrierSelectOptions, dayOptions, channelTypeSelectOptions, shippingMethodSelectOptions, stateSelectOptions, stateSelectOptionsWithAll, i18n]);

    const isCarrierType = scheduleType === SCHEDULE_TYPE_CARRIER;
    const isShippingMethodType = scheduleType === SCHEDULE_TYPE_SHIPPING_METHOD;
    const isCrossCarrierType = scheduleType === SCHEDULE_TYPE_CROSS_CARRIER;

    const getValidationErrors = (data, carrierType, shippingMethodType, editing) => {
        const errs = {};
        if (carrierType) {
            if (!data.carrier_name) errs.carrier_name = true;
        } else if (shippingMethodType) {
            if (!data.shipping_method) errs.shipping_method = true;
        } else {
            const hasChannel = data.channel_type && data.channel_type.id !== NO_FILTER_OPTION_ID;
            if (!hasChannel) errs.cross_carrier = true;
        }
        if (editing) {
            if (data.dispatch_day === null || data.dispatch_day === undefined) errs.dispatch_day = true;
        } else {
            if (data.dispatch_days.size === 0) errs.dispatch_day = true;
        }
        if (!data.dispatch_time) errs.dispatch_time = true;
        const num = parseInt(data.time_before_value, 10);
        if (!num || num <= 0) errs.time_before_dispatch = true;
        return errs;
    };

    const isFormValid = useMemo(() => {
        if (!warehouseTimeZone) return false;
        return Object.keys(getValidationErrors(formData, isCarrierType, isShippingMethodType, isEditing)).length === 0;
    }, [formData, isCarrierType, isShippingMethodType, isEditing, warehouseTimeZone]);

    const validateForm = () => {
        const newErrors = getValidationErrors(formData, isCarrierType, isShippingMethodType, isEditing);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const baseData = {
            dispatch_time: buildDispatchTime(formData.dispatch_time, warehouseTimeZone),
            time_before_dispatch: buildISO8601Duration(formData.time_before_value, formData.time_before_unit),
            carrier_name: isCarrierType ? formData.carrier_name.id : null,
            channel_type_id: isCrossCarrierType
                ? (formData.channel_type && formData.channel_type.id !== NO_FILTER_OPTION_ID ? formData.channel_type.id : null)
                : null,
            shipping_method_id: isShippingMethodType ? formData.shipping_method.id : null,
        };

        if (isEditing) {
            const shippingState = formData.shipping_state && formData.shipping_state.id !== NO_FILTER_OPTION_ID
                ? formData.shipping_state.id
                : null;
            onSubmit({ ...baseData, shipping_state: shippingState, dispatch_day: formData.dispatch_day.id });
        } else {
            const shippingStates = Array.isArray(formData.shipping_state) && formData.shipping_state.length > 0
                ? formData.shipping_state.map(s => s.id)
                : null;
            const days = Array.from(formData.dispatch_days);
            onSubmit({ ...baseData, shipping_state: shippingStates, dispatch_days: days });
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setOpen(false);
        }
    };

    const handleScheduleTypeChange = (newType) => {
        setScheduleType(newType);
        if (newType === SCHEDULE_TYPE_CARRIER) {
            setFormData(prev => ({ ...prev, channel_type: null, shipping_method: null }));
        } else if (newType === SCHEDULE_TYPE_SHIPPING_METHOD) {
            setFormData(prev => ({ ...prev, carrier_name: null, channel_type: null }));
        } else {
            setFormData(prev => ({ ...prev, carrier_name: null, shipping_method: null }));
        }
        setErrors({});
    };

    const selectedUnitOption = unitOptions.find(u => u.id === formData.time_before_unit) || unitOptions[1];

    const formCutoffTime = useMemo(() => {
        if (!formData.dispatch_time || !formData.time_before_value) return null;
        const durationISO = buildISO8601Duration(formData.time_before_value, formData.time_before_unit);
        return calculateCutoffTime(formData.dispatch_time, durationISO);
    }, [formData.dispatch_time, formData.time_before_value, formData.time_before_unit]);

    const timeBeforeWarning = useMemo(() => {
        if (!formData.time_before_value) return null;
        const num = parseInt(formData.time_before_value, 10);
        if (!num || num <= 0) return null;
        const totalMinutes = formData.time_before_unit === 'hours' ? num * 60 : num;
        if (formCutoffTime?.daysBefore > 0) return 'days_before';
        if (totalMinutes > 120) return 'over_2h';
        return null;
    }, [formData.time_before_value, formData.time_before_unit, formCutoffTime]);

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                initialFocus={cancelButtonRef}
                onClose={handleClose}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                    {isEditing
                                        ? i18n.t(`${T_PREFIX}.edit_schedule`)
                                        : i18n.t(`${T_PREFIX}.add_schedule`)
                                    }
                                </Dialog.Title>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-500"
                                    onClick={handleClose}
                                >
                                    <XIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    {/* Schedule Type Selector */}
                                    {!isEditing && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {i18n.t(`${T_PREFIX}.form.schedule_type`)}
                                            </label>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleScheduleTypeChange(SCHEDULE_TYPE_CARRIER)}
                                                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                                                        isCarrierType
                                                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {i18n.t(`${T_PREFIX}.form.type_carrier`)}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleScheduleTypeChange(SCHEDULE_TYPE_SHIPPING_METHOD)}
                                                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                                                        isShippingMethodType
                                                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {i18n.t(`${T_PREFIX}.form.type_shipping_method`)}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleScheduleTypeChange(SCHEDULE_TYPE_CROSS_CARRIER)}
                                                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                                                        isCrossCarrierType
                                                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {i18n.t(`${T_PREFIX}.form.type_cross_carrier`)}
                                                </button>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-400">
                                                {isCarrierType
                                                    ? i18n.t(`${T_PREFIX}.form.type_carrier_help`)
                                                    : isShippingMethodType
                                                        ? i18n.t(`${T_PREFIX}.form.type_shipping_method_help`)
                                                        : i18n.t(`${T_PREFIX}.form.type_cross_carrier_help`)
                                                }
                                            </p>
                                        </div>
                                    )}

                                    {/* Carrier Name - only for carrier type */}
                                    {isCarrierType && (
                                        <div>
                                            {isEditing ? (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {i18n.t(`${T_PREFIX}.form.carrier_name`)}
                                                    </label>
                                                    <p className="text-sm text-gray-900 bg-gray-50 rounded-md px-3 py-2">
                                                        {formData.carrier_name?.name || formData.carrier_name?.id || '-'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    <SelectInput
                                                        label={i18n.t(`${T_PREFIX}.form.carrier_name`)}
                                                        options={carrierSelectOptions}
                                                        value={formData.carrier_name}
                                                        onChange={(value) => {
                                                            setFormData(prev => ({ ...prev, carrier_name: value }));
                                                            if (errors.carrier_name) setErrors(prev => ({ ...prev, carrier_name: false }));
                                                        }}
                                                        className="w-full"
                                                        required
                                                    />
                                                    {errors.carrier_name && (
                                                        <p className="mt-1 text-xs text-red-500">{i18n.t(`${T_PREFIX}.form.select_carrier`)}</p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Shipping Method - only for shipping method type */}
                                    {isShippingMethodType && (
                                        <div>
                                            {isEditing ? (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {i18n.t(`${T_PREFIX}.form.shipping_method`)}
                                                    </label>
                                                    <p className="text-sm text-gray-900 bg-gray-50 rounded-md px-3 py-2">
                                                        {formData.shipping_method?.name || formData.shipping_method?.id || '-'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    <SelectInput
                                                        label={i18n.t(`${T_PREFIX}.form.shipping_method`)}
                                                        options={shippingMethodSelectOptions}
                                                        value={formData.shipping_method}
                                                        onChange={(value) => {
                                                            setFormData(prev => ({ ...prev, shipping_method: value }));
                                                            if (errors.shipping_method) setErrors(prev => ({ ...prev, shipping_method: false }));
                                                        }}
                                                        className="w-full"
                                                        required
                                                    />
                                                    {errors.shipping_method && (
                                                        <p className="mt-1 text-xs text-red-500">{i18n.t(`${T_PREFIX}.form.select_shipping_method`)}</p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Channel Type - for cross-carrier type */}
                                    {isCrossCarrierType && (
                                        <div>
                                            <SelectInput
                                                label={i18n.t(`${T_PREFIX}.form.channel_type`)}
                                                options={channelTypeSelectOptions}
                                                value={formData.channel_type}
                                                onChange={(value) => {
                                                    setFormData(prev => ({ ...prev, channel_type: value }));
                                                    if (errors.cross_carrier) setErrors(prev => ({ ...prev, cross_carrier: false }));
                                                }}
                                                className="w-full"
                                                required
                                            />
                                            {errors.cross_carrier && (
                                                <p className="mt-1 text-xs text-red-500">{i18n.t(`${T_PREFIX}.form.cross_carrier_requirement`)}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Dispatch Day */}
                                    <div>
                                        {isEditing ? (
                                            <>
                                                <SelectInput
                                                    label={i18n.t(`${T_PREFIX}.form.dispatch_day`)}
                                                    options={dayOptions}
                                                    value={formData.dispatch_day}
                                                    onChange={(value) => {
                                                        setFormData(prev => ({ ...prev, dispatch_day: value }));
                                                        if (errors.dispatch_day) setErrors(prev => ({ ...prev, dispatch_day: false }));
                                                    }}
                                                    className="w-full"
                                                    required
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {i18n.t(`${T_PREFIX}.form.dispatch_days`)} <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex flex-wrap gap-2">
                                                    {dayOptions.map(day => {
                                                        const isSelected = formData.dispatch_days.has(day.id);
                                                        return (
                                                            <button
                                                                key={day.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setFormData(prev => {
                                                                        const next = new Set(prev.dispatch_days);
                                                                        if (next.has(day.id)) next.delete(day.id);
                                                                        else next.add(day.id);
                                                                        return { ...prev, dispatch_days: next };
                                                                    });
                                                                    if (errors.dispatch_day) setErrors(prev => ({ ...prev, dispatch_day: false }));
                                                                }}
                                                                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                                                    isSelected
                                                                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                                                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                {day.name}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <div className="flex gap-2 mt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, dispatch_days: new Set(DAYS_OF_WEEK) }));
                                                            if (errors.dispatch_day) setErrors(prev => ({ ...prev, dispatch_day: false }));
                                                        }}
                                                        className="text-xs text-indigo-600 hover:text-indigo-800"
                                                    >
                                                        {i18n.t(`${T_PREFIX}.form.select_all_days`)}
                                                    </button>
                                                    <span className="text-xs text-gray-300">|</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, dispatch_days: new Set([1, 2, 3, 4, 5]) }));
                                                            if (errors.dispatch_day) setErrors(prev => ({ ...prev, dispatch_day: false }));
                                                        }}
                                                        className="text-xs text-indigo-600 hover:text-indigo-800"
                                                    >
                                                        {i18n.t(`${T_PREFIX}.form.select_weekdays`)}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                        {errors.dispatch_day && (
                                            <p className="mt-1 text-xs text-red-500">{i18n.t(`${T_PREFIX}.form.select_day`)}</p>
                                        )}
                                    </div>

                                    {/* Dispatch Time */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {i18n.t(`${T_PREFIX}.form.dispatch_time`)} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="time"
                                            value={formData.dispatch_time}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, dispatch_time: e.target.value }));
                                                if (errors.dispatch_time) setErrors(prev => ({ ...prev, dispatch_time: false }));
                                            }}
                                            className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                errors.dispatch_time
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                            }`}
                                        />
                                    </div>

                                    {/* Time Before Dispatch */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {i18n.t(`${T_PREFIX}.form.time_before_dispatch`)} <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex gap-3">
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.time_before_value}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, time_before_value: e.target.value }));
                                                    if (errors.time_before_dispatch) setErrors(prev => ({ ...prev, time_before_dispatch: false }));
                                                }}
                                                placeholder={i18n.t(`${T_PREFIX}.form.time_before_value`)}
                                                className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                    errors.time_before_dispatch
                                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                }`}
                                            />
                                            <div className="w-40 flex-shrink-0">
                                                <SelectInput
                                                    options={unitOptions}
                                                    value={selectedUnitOption}
                                                    onChange={(value) => {
                                                        setFormData(prev => ({ ...prev, time_before_unit: value.id }));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {errors.time_before_dispatch && (
                                            <p className="mt-1 text-xs text-red-500">{i18n.t(`${T_PREFIX}.form.time_before_dispatch`)}</p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-400">{i18n.t(`${T_PREFIX}.form.time_before_help`)}</p>
                                        {formCutoffTime && (
                                            <p className="mt-1 text-xs text-indigo-500 font-medium">
                                                {i18n.t(`${T_PREFIX}.form.cutoff_preview`, { time: formCutoffTime.time })}
                                                {formCutoffTime.daysBefore > 0 && (
                                                    <span className="text-orange-600">
                                                        {' '}({i18n.t(`${T_PREFIX}.cutoff_days_before`, { count: formCutoffTime.daysBefore })})
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                        {timeBeforeWarning && (
                                            <p className="mt-1 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 font-medium">
                                                {timeBeforeWarning === 'days_before'
                                                    ? i18n.t(`${T_PREFIX}.form.warning_days_before`)
                                                    : i18n.t(`${T_PREFIX}.form.warning_over_2h`)
                                                }
                                            </p>
                                        )}
                                    </div>

                                    {/* Shipping State (optional - available for both types) */}
                                    {stateSelectOptions.length > 0 && (
                                        <div>
                                            {isEditing ? (
                                                <SelectInput
                                                    label={i18n.t(`${T_PREFIX}.form.shipping_state`)}
                                                    options={stateSelectOptionsWithAll}
                                                    value={formData.shipping_state}
                                                    onChange={(value) => {
                                                        setFormData(prev => ({ ...prev, shipping_state: value }));
                                                    }}
                                                    className="w-full"
                                                />
                                            ) : (
                                                <MultiSelectInput
                                                    label={i18n.t(`${T_PREFIX}.form.shipping_state`)}
                                                    options={stateSelectOptions}
                                                    value={formData.shipping_state}
                                                    placeholder={i18n.t(`${T_PREFIX}.form.all_states`)}
                                                    onChange={(value) => {
                                                        setFormData(prev => ({ ...prev, shipping_state: value }));
                                                    }}
                                                    className="w-full"
                                                />
                                            )}
                                            <p className="mt-1 text-xs text-gray-400">{i18n.t(`${T_PREFIX}.form.select_shipping_state`)}</p>
                                        </div>
                                    )}

                                </div>

                                {/* Buttons */}
                                <div className="mt-6 flex justify-end space-x-3">
                                    <Button
                                        type="secondary"
                                        onClick={handleClose}
                                        disabled={isLoading}
                                        className="px-4"
                                    >
                                        {i18n.t(`${T_PREFIX}.form.cancel`)}
                                    </Button>
                                    <Button
                                        type="primary"
                                        buttonType="submit"
                                        loading={isLoading}
                                        disabled={!isFormValid}
                                        className="px-4"
                                    >
                                        {isEditing
                                            ? i18n.t(`${T_PREFIX}.form.update`)
                                            : i18n.t(`${T_PREFIX}.form.create`)
                                        }
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
